import { httpClient } from '@/core/api/httpClient';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import type {
  ProjectField,
  ProjectFieldFilters,
  ProjectFieldPagedResult,
  FieldValue,
} from '../types';
import type {
  CreateFieldInput,
  UpdateFieldInput,
  UpsertFieldValueInput,
  EditFieldOptionInput,
} from '../schemas';
import { mockProjectFields, mockFieldValues } from './custom-fields.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

const mockFields = [...mockProjectFields];

// ------------------------------------------------------------------
// defaultValue JSON boundary
//
// The backend stores `ProjectField.DefaultValue` as an opaque string but requires
// it to be valid JSON (`JsonDocument.Parse` in `ProjectField.Create`). The form
// holds it as a plain string (e.g. `2026-07-01`, `hola`, `true`, `5`), which is not
// valid JSON for date/text/select — so it must be JSON-encoded on the wire and
// decoded back on read. Only the real (non-mock) path crosses that boundary.
// ------------------------------------------------------------------

/** Encode a form `defaultValue` as JSON for the backend (leaves null/empty untouched). */
function encodeDefaultValue<T extends { defaultValue?: string | null }>(payload: T): T {
  if (payload.defaultValue == null || payload.defaultValue === '') return payload;
  return { ...payload, defaultValue: JSON.stringify(payload.defaultValue) };
}

/** Decode a backend `defaultValue` back to a plain string; tolerant of legacy bare values. */
function decodeDefaultValue(raw: string | null | undefined): string | null {
  if (raw == null || raw === '') return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed == null ? null : String(parsed);
  } catch {
    return raw; // legacy value stored before JSON-encoding
  }
}

export const customFieldsService = {
  getProjectFields: async (
    projectId: string,
    filters?: ProjectFieldFilters,
    signal?: AbortSignal,
  ): Promise<ProjectFieldPagedResult> => {
    if (USE_MOCK) {
      let items = mockFields.filter((f) => f.projectId === projectId);

      if (filters?.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        items = items.filter((f) => f.name?.toLowerCase().includes(term));
      }

      const page = filters?.page ?? 1;
      const pageSize = filters?.pageSize ?? 50;
      const totalCount = items.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      const start = (page - 1) * pageSize;

      return {
        items: items.slice(start, start + pageSize),
        page,
        pageSize,
        totalCount,
        totalPages,
        hasPreviousPage: page > 1,
        hasNextPage: page < totalPages,
        firstItemOnPage: totalCount === 0 ? 0 : start + 1,
        lastItemOnPage: Math.min(start + pageSize, totalCount),
      };
    }

    const { data } = await httpClient.get<ProjectFieldPagedResult>(
      `${API_V1}/projects/${projectId}/fields`,
      {
        params: filters ? toPascalCaseParams(filters as Record<string, unknown>) : undefined,
        signal,
      },
    );
    return {
      ...data,
      items: (data.items ?? []).map((f) => ({
        ...f,
        defaultValue: decodeDefaultValue(f.defaultValue),
      })),
    };
  },

  createProjectField: async (projectId: string, payload: CreateFieldInput): Promise<void> => {
    if (USE_MOCK) {
      const newField: ProjectField = {
        id: `field-${Date.now()}`,
        projectId,
        name: payload.name,
        fieldType: String(payload.type),
        required: payload.required ?? false,
        typeConfig: payload.typeConfig ?? null,
        description: payload.description ?? null,
        defaultValue: payload.defaultValue ?? null,
        options:
          payload.options?.map((o, i) => ({
            id: `opt-${Date.now()}-${i}`,
            name: o.name ?? null,
            value: o.value ?? null,
            color: o.color ?? null,
            orderIndex: o.orderIndex ?? i,
          })) ?? null,
        order: payload.order ?? mockFields.length,
        createdAt: new Date().toISOString(),
      };
      mockFields.push(newField);
      return;
    }

    await httpClient.post<CreateFieldInput, void>(
      `${API_V1}/projects/${projectId}/fields`,
      encodeDefaultValue(payload),
    );
  },

  updateProjectField: async (
    projectId: string,
    fieldId: string,
    payload: UpdateFieldInput,
  ): Promise<void> => {
    if (USE_MOCK) {
      const idx = mockFields.findIndex((f) => f.id === fieldId && f.projectId === projectId);
      if (idx !== -1) {
        mockFields[idx] = {
          ...mockFields[idx],
          ...(payload.name !== undefined && { name: payload.name }),
          ...(payload.description !== undefined && { description: payload.description ?? null }),
          ...(payload.defaultValue !== undefined && { defaultValue: payload.defaultValue ?? null }),
          ...(payload.required !== undefined && { required: payload.required }),
          ...(payload.typeConfig !== undefined && { typeConfig: payload.typeConfig ?? null }),
          ...(payload.order !== undefined && { order: payload.order }),
          ...(payload.options !== undefined && {
            options:
              payload.options?.map((o, i) => ({
                id: `opt-${Date.now()}-${i}`,
                name: o.name ?? null,
                value: o.value ?? null,
                color: o.color ?? null,
                orderIndex: o.orderIndex ?? i,
              })) ?? null,
          }),
        };
      }
      return;
    }

    await httpClient.put<UpdateFieldInput, void>(
      `${API_V1}/projects/${projectId}/fields/${fieldId}`,
      encodeDefaultValue(payload),
    );
  },

  deleteProjectField: async (projectId: string, fieldId: string): Promise<void> => {
    if (USE_MOCK) {
      const idx = mockFields.findIndex((f) => f.id === fieldId && f.projectId === projectId);
      if (idx !== -1) mockFields.splice(idx, 1);
      return;
    }

    await httpClient.delete<void>(`${API_V1}/projects/${projectId}/fields/${fieldId}`);
  },

  getTaskFieldValues: async (taskId: string, signal?: AbortSignal): Promise<FieldValue[]> => {
    if (USE_MOCK) {
      return mockFieldValues.filter((v) => v.taskId === taskId);
    }

    const { data } = await httpClient.get<FieldValue[]>(`${API_V1}/tasks/${taskId}/field-values`, {
      signal,
    });
    return data;
  },

  /**
   * Batched equivalent of `getTaskFieldValues`, for a list of visible tasks and
   * visible custom-field columns — one request per page load instead of one
   * per (task, field) pair. Only covers the given `fieldIds`, not every field
   * defined on the project.
   */
  getTaskFieldValuesBatch: async (
    projectId: string,
    taskIds: string[],
    fieldIds: string[],
    signal?: AbortSignal,
  ): Promise<Record<string, FieldValue[]>> => {
    if (USE_MOCK) {
      const fieldIdSet = new Set(fieldIds);
      const result: Record<string, FieldValue[]> = {};
      for (const taskId of taskIds) {
        result[taskId] = mockFieldValues.filter(
          (v) => v.taskId === taskId && v.fieldId != null && fieldIdSet.has(v.fieldId),
        );
      }
      return result;
    }

    const { data } = await httpClient.get<Record<string, FieldValue[]>>(
      `${API_V1}/projects/${projectId}/tasks/field-values`,
      { params: { taskIds: taskIds.join(','), fieldIds: fieldIds.join(',') }, signal },
    );
    return data;
  },

  upsertTaskFieldValue: async (
    taskId: string,
    fieldId: string,
    payload: UpsertFieldValueInput,
  ): Promise<void> => {
    if (USE_MOCK) {
      const idx = mockFieldValues.findIndex((v) => v.taskId === taskId && v.fieldId === fieldId);
      if (idx !== -1) {
        mockFieldValues[idx] = {
          ...mockFieldValues[idx],
          value: payload.value,
          updatedAt: new Date().toISOString(),
        };
      } else {
        mockFieldValues.push({
          id: `val-${Date.now()}`,
          taskId,
          fieldId,
          value: payload.value,
          updatedAt: new Date().toISOString(),
        });
      }
      return;
    }

    await httpClient.put<UpsertFieldValueInput, void>(
      `${API_V1}/tasks/${taskId}/field-values/${fieldId}`,
      payload,
    );
  },

  updateFieldOption: async (
    projectId: string,
    fieldId: string,
    optionId: string,
    payload: EditFieldOptionInput,
  ): Promise<void> => {
    if (USE_MOCK) {
      const field = mockFields.find((f) => f.id === fieldId && f.projectId === projectId);
      const option = field?.options?.find((o) => o.id === optionId);
      if (option) {
        if (payload.name !== undefined) option.name = payload.name;
        if (payload.value !== undefined) option.value = payload.value;
        if (payload.color !== undefined) option.color = payload.color;
        if (payload.orderIndex !== undefined) option.orderIndex = payload.orderIndex;
      }
      return;
    }

    await httpClient.put<EditFieldOptionInput, void>(
      `${API_V1}/projects/${projectId}/fields/${fieldId}/options/${optionId}`,
      payload,
    );
  },

  deleteFieldOption: async (
    projectId: string,
    fieldId: string,
    optionId: string,
  ): Promise<void> => {
    if (USE_MOCK) {
      const field = mockFields.find((f) => f.id === fieldId && f.projectId === projectId);
      if (field?.options) {
        field.options = field.options.filter((o) => o.id !== optionId);
      }
      return;
    }

    await httpClient.delete<void>(
      `${API_V1}/projects/${projectId}/fields/${fieldId}/options/${optionId}`,
    );
  },

  uploadTaskFieldValueFile: async (
    taskId: string,
    fieldId: string,
    file: File,
  ): Promise<FieldValue> => {
    if (USE_MOCK) {
      const mockValue: FieldValue = {
        id: `val-${Date.now()}`,
        taskId,
        fieldId,
        value: JSON.stringify({
          storageKey: `customfields/mock/${file.name}`,
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
        }),
        updatedAt: new Date().toISOString(),
      };
      const idx = mockFieldValues.findIndex((v) => v.taskId === taskId && v.fieldId === fieldId);
      if (idx !== -1) mockFieldValues[idx] = mockValue;
      else mockFieldValues.push(mockValue);
      return mockValue;
    }

    const formData = new FormData();
    formData.append('file', file);
    const { data } = await httpClient.post<FormData, FieldValue>(
      `${API_V1}/tasks/${taskId}/field-values/${fieldId}/file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },
};
